import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Phone, MapPin, CalendarDays, Truck, Clock, Mail, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = ["8:00–10:00 AM", "8:00–10:00 PM"];
const fmt = (m) => `${((Math.floor(m / 60) + 11) % 12) + 1}:${String(m % 60).padStart(2, "0")} ${m < 720 ? "AM" : "PM"}`;
const PICKUP = Array.from({ length: 23 }, (_, i) => 540 + i * 30).map((m) => ({ v: `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`, label: fmt(m) }));

const FIELDS = ["customer_name", "customer_phone", "fulfillment_date", "pickup_time", "delivery_time", "delivery_method", "address", "notes"];

export const OrderDetailsEditor = ({ order, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const start = () => { setForm(Object.fromEntries(FIELDS.map((k) => [k, order[k] || ""]))); setEditing(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, form);
      onSaved(data); setEditing(false); toast.success("Order details updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (!editing) return (
    <div className="rounded-2xl border border-sugar-border bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-sugar-ink">Customer & fulfillment</h3>
        <button data-testid="admin-edit-details" onClick={start} className="inline-flex items-center gap-1 text-xs font-bold text-sugar-pink hover:underline"><Pencil size={13} /> Edit</button>
      </div>
      <ul className="mt-3 space-y-2 text-sm text-sugar-ink2">
        <li data-testid="admin-detail-name" className="font-bold text-sugar-ink">{order.customer_name}</li>
        <li className="flex items-center gap-2"><Phone size={15} className="text-sugar-pink" /><a href={`tel:${order.customer_phone}`} className="hover:underline">{order.customer_phone}</a></li>
        {order.customer_email && <li className="flex items-center gap-2"><Mail size={15} className="text-sugar-pink" /><a href={`mailto:${order.customer_email}`} className="hover:underline">{order.customer_email}</a></li>}
        <li className="flex items-center gap-2"><Truck size={15} className="text-sugar-pink" />{order.delivery_method === "pickup" ? "Pickup in Langley" : "Delivery"}</li>
        <li data-testid="admin-detail-date" className="flex items-center gap-2"><CalendarDays size={15} className="text-sugar-pink" />{order.fulfillment_date}</li>
        {order.missing_time
          ? <li data-testid="admin-detail-missing-time" className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 font-bold text-red-600"><AlertTriangle size={15} />No {order.delivery_method === "pickup" ? "pickup time" : "delivery window"} chosen — follow up with the customer</li>
          : <li data-testid="admin-detail-time" className="flex items-center gap-2"><Clock size={15} className="text-sugar-pink" />{order.pickup_time || order.delivery_time}</li>}
        {order.delivery_policy_agreed && <li data-testid="admin-detail-policy" className="flex items-start gap-2 text-xs"><ShieldCheck size={15} className="shrink-0 text-emerald-600" />Agreed to delivery policy: meets driver at the front door / accessible pickup point</li>}
        {order.address && <li className="flex items-center gap-2"><MapPin size={15} className="text-sugar-pink" />{order.address}</li>}
      </ul>
      {order.notes && <p className="mt-3 rounded-xl bg-sugar-cream px-3 py-2 text-sm text-sugar-ink2">“{order.notes}”</p>}
    </div>
  );

  const field = (k, label, type = "text") => (
    <label className="block"><span className="mb-1 block text-xs font-bold text-sugar-ink">{label}</span>
      <Input data-testid={`admin-edit-${k}`} type={type} value={form[k]} onChange={set(k)} className="rounded-xl border-sugar-border" /></label>
  );
  return (
    <div className="space-y-3 rounded-2xl border-2 border-sugar-pink/40 bg-white p-5">
      <h3 className="font-display text-lg font-bold text-sugar-ink">Edit details</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("customer_name", "Name")}
        {field("customer_phone", "Phone")}
        {field("fulfillment_date", "Date", "date")}
        {form.delivery_method === "pickup" ? (
          <label className="block"><span className="mb-1 block text-xs font-bold text-sugar-ink">Pickup time</span>
            <select data-testid="admin-edit-pickup_time" value={form.pickup_time} onChange={set("pickup_time")} className="h-10 w-full rounded-xl border border-sugar-border bg-white px-3 text-sm">
              <option value="">Not set</option>
              {PICKUP.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              {form.pickup_time && !PICKUP.some((p) => p.v === form.pickup_time) && <option value={form.pickup_time}>{form.pickup_time}</option>}
            </select></label>
        ) : (
          <label className="block"><span className="mb-1 block text-xs font-bold text-sugar-ink">Delivery window</span>
            <select data-testid="admin-edit-delivery_time" value={form.delivery_time} onChange={set("delivery_time")} className="h-10 w-full rounded-xl border border-sugar-border bg-white px-3 text-sm">
              <option value="">Not set</option>
              {WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}
              {form.delivery_time && !WINDOWS.includes(form.delivery_time) && <option value={form.delivery_time}>{form.delivery_time}</option>}
            </select></label>
        )}
        <label className="block"><span className="mb-1 block text-xs font-bold text-sugar-ink">Pickup or delivery</span>
          <select data-testid="admin-edit-delivery_method" value={form.delivery_method} onChange={set("delivery_method")} className="h-10 w-full rounded-xl border border-sugar-border bg-white px-3 text-sm">
            <option value="pickup">Pickup</option><option value="delivery">Delivery</option>
          </select></label>
        {field("address", "Address")}
      </div>
      <label className="block"><span className="mb-1 block text-xs font-bold text-sugar-ink">Notes</span>
        <Textarea data-testid="admin-edit-notes" value={form.notes} onChange={set("notes")} className="rounded-xl border-sugar-border" /></label>
      <div className="flex gap-2">
        <Button data-testid="admin-save-details" disabled={saving} onClick={save} className="rounded-full bg-sugar-pink font-bold text-white hover:bg-sugar-pinkhover">{saving ? "Saving…" : "Save changes"}</Button>
        <Button data-testid="admin-cancel-details" variant="ghost" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
};
